((L, U) => {
	'use strict';

	const reg = {
		args: /(['"].*?['"]|[^'",\s]+)(?=\s*,|\s*$)/g,
		entities: /[&<>"]/g,
		ext: /(.[^(]+)(?:\((.*)\))?/,
		pair: /{{#(\S+?)(?:\|(.+?))?}}([\s\S]+?){{\/\1}}/g,
		partial: /{{\s*>(.+?)}}/g,
		short: /{{([^}]+) +\? ([^}]+)}}/g,
		single: /{{(.+?)}}/g,
		str: /^\\?(["'])/,
		tags: /{{\s*(?:([#/])([^#{|\n\s]+)\s*(\|[^{\n]+)?|else)\s*}}/g
	};
	const entities = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;'
	};
	const helpers = {
		is(a, b) {
			return a === (b !== U ? b : this.val);
		},
		not(a, b) {
			return ! helpers.is.call(this, a, b);
		},
		isEmpty(val) {
			return val !== U ?
				_isEmpty(val) : this.empty;
		},
		notEmpty(val) {
			return ! helpers.isEmpty.call(this, val);
		}
	};
	const global = {};
	const views = {};

	/**
	 * Determine if value matches empty criteria
	 *
	 * @private
	 * @param {*} val
	 * @returns {Boolean}
	 */
	const _isEmpty = val => {
		return val === '' || val === false || val == null ||
			(typeof val === 'object' && ! Object.keys(val).length);
	};

	/**
	 * Make recursive partial replacements
	 *
	 * @private
	 * @param {String} temp
	 * @returns {String}
	 */
	const _embed = temp => {
		temp = temp.replace(reg.partial, (m, tag) => views[tag.trim()] || '');

		if (reg.partial.test(temp)) {
			temp = _embed(temp);
		}

		return temp;
	};

	/**
	 * Get specific object value
	 *
	 * @private
	 * @param {Object} data
	 * @param {Object} prev
	 * @param {String} key
	 * @param {String} fb
	 * @param {Object} init
	 * @returns {*}
	 */
	const _get = (data, prev, key, fb, init) => {
		key = key.trim();

		// Alter context
		if (key.startsWith('$root.')) {
			key = key.slice(6);
			data = init;
		} else if (key.startsWith('../')) {
			key = key.slice(3);
			data = prev;
		}

		const segs = key === '.' ?
			['.'] : key.split('.');
		const orig = data;
		const count = segs.length - 1;

		// Loop through object segments
		for (let i = 0; i <= count; i++) {
			key = segs[i];

			if (data && data.hasOwnProperty(key)) {
				data = data[key];

				// Return value on last segment
				if (i === count && ! _isEmpty(data)) {
					if (typeof data === 'function') {
						return data.call({
							data: orig,
							root: init
						});
					}

					return data;
				}
			} else {
				break;
			}
		}

		if (key === '.') {
			return data;
		}

		// Process fallback value
		if (fb && fb !== '') {
			return _string(fb) || _get(orig, prev, fb, '', init);
		}

		return fb || (
			L.$isObject(data) ||
			Array.isArray(data) ?
				data[key] : data
		);
	};

	/**
	 * Process value based on quote enclosure
	 *
	 * @private
	 * @param {String} val
	 * @param {Boolean} [wrap=false]
	 * @returns {String}
	 */
	const _string = (val, wrap) => {
		val = val.trim();

		const match = val.match(reg.str);

		if (match) {
			return val.replace(reg.str, '')
				.replace(new RegExp(match[0] + '$'), '');
		}

		if (wrap) {
			return '{{' + val + '}}';
		}
	};

	/**
	 * Parse helper arguments
	 *
	 * @private
	 * @param {String} str
	 * @param {Object} data
	 * @param {Object} prev
	 * @param {Object} init
	 * @returns {*}
	 */
	const _parseArgs = (str, data, prev, init) => {
		const args = str !== U ?
			str.match(reg.args) || [] : [];

		return args.map(arg => {
			arg = arg.trim();

			const match = arg.match(reg.str);

			if (match) {
				return arg.replace(reg.str, '')
					.replace(new RegExp(match[0] + '$'), '');
			}

			arg = L._castString(arg);

			return typeof arg === 'string' ?
				_get(data, prev, arg, '', init) :
				arg;
		});
	};

	/**
	 * Parse template string
	 *
	 * @private
	 * @param {String} temp
	 * @param {Object} data
	 * @param {Object} prev
	 * @param {Object} init
	 * @param {Number} index
	 * @returns {String}
	 */
	const _parse = (temp, data, prev, init, index) => {
		return _parseSingle(
			_parsePair(temp, data, prev, init, index), data, prev, init, index
		);
	};

	/**
	 * Replace single template tags
	 *
	 * @private
	 * @param {String} temp
	 * @param {Object} data
	 * @param {Object} prev
	 * @param {Object} init
	 * @param {Number} index
	 * @returns {String}
	 */
	const _parseSingle = (temp, data, prev, init, index) => {
		return temp.replace(reg.single, (m, set) => {
			const split = set.split('||');
			const fb = split[1];
			const segs = split[0].split('|');
			const tag = segs[0].trim();

			let val = _get(data, prev, tag, fb, init);

			// Process helpers
			for (let i = 0; i < segs.length; i++) {
				const el = segs[i];
				const arr = el.match(reg.ext);

				segs[i] = el.trim();

				if (arr) {
					const helper = helpers[arr[1].trim()];

					if (helper && (segs.length > 1 || ! data.hasOwnProperty(tag))) {
						val = helper.apply({
							val: val,
							data: data,
							root: init,
							tag: tag,
							index: index,
							fallback: fb
						}, _parseArgs(arr[2], data, prev, init));
					}
				}
			}

			// Encode output by default
			if (val === U || typeof val === 'object') {
				val = '';
			} else if (typeof val === 'string') {
				// Recursively process injected tags
				if (val.includes('{{')) {
					val = _render(val, data);
				}

				// Encode HTML characters
				if (! segs.includes('raw')) {
					val = val.replace(reg.entities, char => entities[char]);
				}
			}

			return val;
		});
	};

	/**
	 * Replace template tag pairs
	 *
	 * @private
	 * @param {String} temp
	 * @param {Object} data
	 * @param {Object} prev
	 * @param {Object} init
	 * @param {Number} index
	 * @returns {String}
	 */
	const _parsePair = (temp, data, prev, init, index) => {
		return temp.replace(reg.pair, (m, t, helper, inner) => {
			const tag = t.replace(/%\d+/, '');
			const cond = inner.split('{{:' + t + '}}');
			const help = [];

			inner = cond[0];

			let val = _get(data, prev, tag, U, init);
			let empty = _isEmpty(val);
			let each;
			let sec;

			// Parse helpers
			if (helper || empty) {
				const methods = helper ?
					helper.split('|') : [];
				const agg = [];

				sec = cond.length > 1 ?
					cond[1] : '';

				// Check for root helpers
				if (empty) {
					const arg = methods[0] && methods[0][0] === '(';

					methods.unshift(tag + (arg ? methods[0] : '()'));

					if (arg) {
						methods.splice(1, 1);
					}
				}

				// Capture available aggregates and helpers
				for (let i = 0, len = methods.length; i < len; i++) {
					const arr = methods[i].match(reg.ext);
					const name = arr[1].trim();

					// Check for each helper
					if (name === 'each') {
						each = true;
					} else if (helpers[name]) {
						const pair = [helpers[name], arr[2]];

						each ?
							help.push(pair) :
							agg.push(pair);
					}
				}

				// Process aggregates
				if (agg.length) {
					if (! agg.every(f => {
						const rv = f[0].apply({
							val: val,
							data: data,
							root: init,
							tag: tag,
							empty: empty,
							index: index
						}, _parseArgs(f[1], val, prev, init));

						if (rv === false) {
							return rv;
						}

						if (rv !== true) {
							val = rv;
							empty = _isEmpty(val);
						}

						return true;
					})) {
						return _parse(sec, data, prev, init, index);
					}
				} else if (empty) {
					return _parse(sec, data, prev, init, index);
				}
			}

			// Process parent context on reserved tag pairs, excluding $root
			if (tag[0] === '$' && tag[1] !== 'r') {
				val = data;
			}

			const isObject = typeof val === 'object';

			if (! each) {
				if (! isObject) {
					val = Object.assign({}, data, {
						'.': val,
						'#': 0,
						'##': 1
					});
				}

				return _parse(inner, val, data, init, index);
			}

			if (! empty) {
				// Loop through objects and arrays
				if (isObject) {
					const isPlainObject = L.$isObject(val);
					const keys = Object.keys(val);
					const count = keys.length;
					const resp = [];

					for (let i = 0; i < count; i++) {
						const key = keys[i];

						let el = val[key];

						empty = _isEmpty(el);

						let cont = help.every(f => {
							const rv = f[0].apply({
								val: el,
								data: data,
								root: init,
								tag: tag,
								empty: empty,
								index: i
							}, _parseArgs(f[1], el, prev, init));

							if (rv === false) {
								return rv;
							}

							if (rv !== true) {
								el = rv;
							}

							return true;
						});

						// Merge default properties
						const item = Object.assign({}, L.$isObject(el) ?
							el :
							(isPlainObject ? val : {}),
						{
							$first: ! i,
							$index0: i,
							$index: i + 1,
							$key: key,
							$last: i + 1 === count,
							'.': el
						});

						cont = (helper.length && cont) || (! helper.length && ! empty);

						if (cont || sec) {
							resp.push(_parse(cont ? inner : sec, item, data, init, i));
						}
					}

					return resp.join('');
				}

				return inner;
			}

			return '';
		});
	};

	/**
	 * Render template string
	 *
	 * @private
	 * @param {String} temp
	 * @param {Object} data
	 * @returns {String}
	 */
	const _render = (temp, data) => {
		const depth = [];
		const tags = {};

		// Make partial replacements
		temp = _embed(temp);

		// Match tag pairs
		temp = temp.replace(reg.short, (m, cond, val) => {
			cond = cond.trim() + '}}';

			const segs = val.split(' : ');

			let resp = '{{#' + cond + _string(segs[0], true);

			if (segs.length > 1) {
				resp += '{{else}}' + _string(segs[1], true);
			}

			return resp + '{{/' + cond;
		}).replace(reg.tags, (m, pre, tag, helper) => {
			let resp = '{{';

			if (pre) {
				const segs = tag.split('(');
				const root = segs[0];
				const exists = tags.hasOwnProperty(tag);

				resp += pre + root + '%';

				if (pre === '#') {
					depth.push(root);

					if (! exists) {
						tags[root] = {
							i: 0,
							o: []
						};
					}

					tags[root].i++;
					tags[root].o.push(tags[root].i);

					if (segs.length > 1) {
						helper = '(' + segs.slice(1).join('(');
					}

					resp += tags[root].i + (helper || '');
				} else if (exists) {
					resp += tags[root].o.pop();

					tags[root].i--;

					depth.pop();
				}
			} else if (depth.length) {
				tag = depth.slice(-1)[0];

				resp += ':' + tag + '%' + tags[tag].i;
			}

			return resp + '}}';
		});

		// Parse template tags
		return _parse(temp, data, {}, data, 0);
	};

	L.app = {
		/**
		 * Application instance storage
		 */
		fn: {},

		/**
		 * Create an application
		 *
		 * @param {String} name
		 * @param {Object} options
		 * @param {Object} options.model
		 * @param {$|HTMLElement|String} [options.target]
		 * @param {$|HTMLElement|String} options.view
		 * @returns {Object}
		 */
		make(name, options) {
			const conf = L.$extend(true, {
				init: true,
				events: {},
				model: {}
			}, options);
			const app = L.app;

			let current;
			let past;
			let parsed;
			let views;
			let fn;

			// Create a new application controller
			app[name] = new (L._make(name, {}, {}, false, conf.model))();

			views = L.$(conf.target || conf.view).map(el => {
				L.$exec(conf.events.init, {
					args: [el, app[name]]
				});

				return [el, conf.target ? conf.view : el.outerHTML];
			});

			fn = data => {
				current = JSON.stringify(data);

				if (current === past) {
					return;
				}

				past = current;

				views.forEach(view => {
					parsed = L.$parseHTML(
						L.view.render(view[1], data)
					);

					L.view.diff(view[0], parsed, conf.target);

					L.$exec(conf.events.render, {
						args: [view[0], app[name]]
					});
				});
			};

			parsed = null;

			if (conf.init) {
				fn(conf.model);
			}

			Object.assign(app[name], {
				/**
				 * Destroy current application
				 */
				$destroy() {
					if (conf._destruct) {
						conf._destruct();
					}

					if (views) {
						views.forEach(view => {
							view[0].remove();
						});
					}

					app[name] = views = null;

					delete app[name];
				},

				/**
				 * Pause view updating
				 */
				$pause() {
					app[name].$unobserve('*');
				},

				/**
				 * Resume view updating
				 *
				 * @param {Boolean} [update=false]
				 */
				$resume(update) {
					app[name].$observe('*', fn);

					if (update) {
						fn(app[name].$get());
					}
				}
			});

			// Initialize app observation
			app[name].$resume();

			return app[name];
		}
	};

	L.view = {
		/**
		 * Parse data into template string
		 *
		 * @param {String} template
		 * @param {Object} [data]
		 * @param {Object} [options]
		 * @param {Boolean} [options.spaceless=false]
		 * @returns {String}
		 */
		render(template, data, options = {}) {
			template = _render(views[template] || template, Object.assign({}, global, data));

			if (options.spaceless !== false) {
				template = template.replace(/>\s+</g, '><');
			}

			return template;
		},

		/**
		 * Add global template variables
		 *
		 * @param {Object|String} a - key or variable object
		 * @param {*} [b] - variable value
		 */
		addGlobal(a, b) {
			L._extend(global, a, b);
		},

		/**
		 * Add conditional template handler or tag data modifier
		 *
		 * @param {Object|String} name
		 * @param {Function} [fn]
		 */
		addHelper(name, fn) {
			L._extend(helpers, name, fn);
		},

		/**
		 * Add views to store for on-demand reference
		 *
		 * @param {Object|String} name
		 * @param {String} [value]
		 */
		addView(name, value) {
			L._extend(views, name, value);
		}
	};
})(Apt, undefined);